// The cover's two stems (convertedUrl = vocals, instrumentalUrl = music),
// decoded once and mixed in the browser: live through gain nodes for the
// preview, offline for the export. audio.jammable.com serves
// access-control-allow-origin: *, so fetch() can read them.

export type PcmAudio = { sampleRate: number; channels: Float32Array[] };

export type Stems = {
  vocals: AudioBuffer | null;
  music: AudioBuffer | null;
  duration: number;
};

const EXPORT_RATE = 48_000;
/** Loudness samples per second, for the pulse the singer photo rides on. */
export const ENVELOPE_RATE = 30;

/** Bytes in so far, of how many (0 while a file's size is unknown). */
export type LoadBytes = (loaded: number, total: number) => void;

/** Fetches the whole file, telling `onBytes` as it arrives. */
async function download(url: string, onBytes?: LoadBytes) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Couldn't load audio (${res.status})`);
  const total = Number(res.headers.get("content-length")) || 0;
  if (!onBytes || !res.body) return res.arrayBuffer();
  const reader = res.body.getReader();
  const parts: Uint8Array[] = [];
  let loaded = 0;
  onBytes(0, total);
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    parts.push(value);
    loaded += value.byteLength;
    onBytes(loaded, Math.max(total, loaded));
  }
  // Done: say so exactly, whatever the declared size was.
  onBytes(loaded, loaded);
  const out = new Uint8Array(loaded);
  let at = 0;
  for (const p of parts) {
    out.set(p, at);
    at += p.byteLength;
  }
  return out.buffer;
}

async function decode(
  ctx: BaseAudioContext,
  url?: string | null,
  onBytes?: LoadBytes,
): Promise<AudioBuffer | null> {
  if (!url) return null;
  return ctx.decodeAudioData(await download(url, onBytes));
}

/** While the cover's audio loads: downloading (with bytes), then decoding. */
export type StemsProgress =
  | { stage: "download"; loaded: number; total: number }
  | { stage: "decode" };

import type {
  VoiceTrack,
  VoiceSegment as Segment,
} from "../../utils/voiceTracks";

// A switch between voices fades over this long each side, so a cut never
// clicks.
const SWITCH_FADE_SECS = 0.02;

/**
 * A cover with several voices: its convertedUrl is only ONE voice singing
 * the whole song (the jammer stores each voice's full track and mixes the
 * cover from them by voiceTimes). Rebuild the vocals the cover actually has:
 * each voice's track, only inside its own stretches.
 */
export function stitchVoices(
  ctx: BaseAudioContext,
  tracks: Map<string, AudioBuffer>,
  segments: Segment[],
): AudioBuffer | null {
  const any = tracks.values().next().value as AudioBuffer | undefined;
  if (!any) return null;
  const rate = any.sampleRate;
  const channels = Math.max(
    ...Array.from(tracks.values(), (b) => b.numberOfChannels),
  );
  const length = Math.max(...Array.from(tracks.values(), (b) => b.length));
  const out = ctx.createBuffer(channels, length, rate);
  const fade = Math.max(1, Math.round(SWITCH_FADE_SECS * rate));
  for (const seg of segments) {
    const src = tracks.get(seg.voice);
    if (!src) continue;
    const a = Math.max(0, Math.floor(seg.start * rate) - fade);
    const b = Math.min(src.length, Math.ceil(seg.end * rate) + fade);
    const inner0 = Math.floor(seg.start * rate),
      inner1 = Math.ceil(seg.end * rate);
    for (let c = 0; c < channels; c++) {
      const from = src.getChannelData(Math.min(c, src.numberOfChannels - 1));
      const to = out.getChannelData(c);
      for (let i = a; i < b; i++) {
        // Ramp in across the start, out across the end.
        const g =
          i < inner0 + fade
            ? Math.min(1, (i - a) / (2 * fade))
            : i > inner1 - fade
              ? Math.min(1, (b - i) / (2 * fade))
              : 1;
        to[i] += from[i] * g;
      }
    }
  }
  return out;
}

export async function loadStems(
  ctx: BaseAudioContext,
  vocalsUrl?: string | null,
  musicUrl?: string | null,
  voices?: { tracks: VoiceTrack[]; segments: Segment[] } | null,
  onProgress?: (p: StemsProgress) => void,
): Promise<Stems> {
  const several =
    !!voices && voices.tracks.length > 1 && voices.segments.length > 0;
  // Every file's bytes summed into one figure; once all are in, it's
  // decoding (the browser gives no progress for that).
  const files = new Map<string, { loaded: number; total: number }>();
  let expected = 0;
  const bytesOf = (key: string): LoadBytes | undefined => {
    if (!onProgress) return undefined;
    expected += 1;
    return (loaded, total) => {
      files.set(key, { loaded, total });
      let l = 0,
        t = 0,
        complete = files.size === expected;
      files.forEach((f) => {
        l += f.loaded;
        t += f.total;
        if (!f.total || f.loaded < f.total) complete = false;
      });
      onProgress(
        complete
          ? { stage: "decode" }
          : { stage: "download", loaded: l, total: t },
      );
    };
  };
  const [music, vocals] = await Promise.all([
    decode(ctx, musicUrl, musicUrl ? bytesOf("music") : undefined),
    several
      ? Promise.all(
          voices!.tracks.map(
            async (t) =>
              [t.voice, await decode(ctx, t.url, bytesOf(t.voice))] as const,
          ),
        ).then((list) => {
          const map = new Map<string, AudioBuffer>();
          list.forEach(([v, b]) => b && map.set(v, b));
          return stitchVoices(ctx, map, voices!.segments);
        })
      : decode(ctx, vocalsUrl, vocalsUrl ? bytesOf("vocals") : undefined),
  ]);
  return {
    vocals,
    music,
    duration: Math.max(vocals?.duration ?? 0, music?.duration ?? 0),
  };
}

/** The trimmed stretch, mixed at the project's gains, as stereo PCM for the encoder. */
export async function mixdown(
  stems: Stems,
  start: number,
  end: number,
  gains: { vocals: number; music: number },
): Promise<PcmAudio> {
  const secs = Math.max(0.1, end - start);
  const ctx = new OfflineAudioContext(
    2,
    Math.ceil(secs * EXPORT_RATE),
    EXPORT_RATE,
  );
  const play = (buffer: AudioBuffer | null, gain: number) => {
    if (!buffer || gain <= 0) return;
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const g = ctx.createGain();
    g.gain.value = gain;
    // Short fades at the cut, so the clip doesn't start or stop on a click.
    const fade = Math.min(0.08, secs / 4);
    g.gain.setValueAtTime(0, 0);
    g.gain.linearRampToValueAtTime(gain, fade);
    g.gain.setValueAtTime(gain, secs - fade);
    g.gain.linearRampToValueAtTime(0, secs);
    src.connect(g).connect(ctx.destination);
    src.start(0, start, secs);
  };
  play(stems.vocals, gains.vocals);
  play(stems.music, gains.music);
  const out = await ctx.startRendering();
  return {
    sampleRate: out.sampleRate,
    channels: [out.getChannelData(0), out.getChannelData(1)],
  };
}

/**
 * Loudness of the whole song, ENVELOPE_RATE values a second, 0..1. Read by
 * the frame drawer so the preview and the export pulse identically.
 */
export function envelopeOf(stems: Stems): Float32Array {
  const n = Math.ceil(stems.duration * ENVELOPE_RATE);
  const env = new Float32Array(n);
  for (const buffer of [stems.vocals, stems.music]) {
    if (!buffer) continue;
    const data = buffer.getChannelData(0);
    const per = buffer.sampleRate / ENVELOPE_RATE;
    for (let i = 0; i < n; i++) {
      let sum = 0;
      const from = Math.floor(i * per),
        to = Math.min(data.length, Math.floor((i + 1) * per));
      for (let k = from; k < to; k += 4) sum += data[k] * data[k];
      env[i] += Math.sqrt(sum / Math.max(1, (to - from) / 4));
    }
  }
  let peak = 0;
  for (const v of env) peak = Math.max(peak, v);
  if (peak > 0) for (let i = 0; i < n; i++) env[i] /= peak;
  return env;
}
